import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Paper,
  Grid,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/api';

interface User {
  id: number;
  name: string;
  email: string;
}

interface Message {
  id: number;
  senderId: number;
  receiverId: number;
  content: string;
  isRead: boolean;
  createdAt: string;
  sender: User;
  receiver: User;
}

interface Conversation {
  otherUserId: number;
  otherUser: User;
  lastMessageAt: string;
  messageCount: number;
  hasUnreadMessages: boolean;
}

const NewMessaging: React.FC = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; conversation: Conversation | null }>({ open: false, conversation: null });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const res = await apiRequest('/messages/conversations');
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  }, []);

  // Load messages for a conversation
  const loadMessages = async (otherUserId: number) => {
    try {
      const res = await apiRequest(`/messages/conversation/${otherUserId}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
        
        // Mark messages as read
        await apiRequest(`/messages/read/${otherUserId}`, {
          method: 'PUT'
        });
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  // Send message
  const sendMessage = async () => {
    if (!selectedConversation || !newMessage.trim()) return;

    setLoading(true);
    try {
      const res = await apiRequest('/messages', {
        method: 'POST',
        body: JSON.stringify({
          receiverId: selectedConversation.otherUserId,
          content: newMessage.trim()
        })
      });

      if (res.ok) {
        setNewMessage('');
        await loadMessages(selectedConversation.otherUserId);
        await loadConversations(); // Refresh conversations
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const selectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.otherUserId);
  };

  // Delete conversation
  const deleteConversation = async () => {
    if (!deleteDialog.conversation) return;

    try {
      const res = await apiRequest(`/messages/conversation/${deleteDialog.conversation.otherUserId}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        // Remove conversation from list
        setConversations(prev => 
          prev.filter(conv => conv.otherUserId !== deleteDialog.conversation?.otherUserId)
        );
        
        // If this was the selected conversation, clear it
        if (selectedConversation?.otherUserId === deleteDialog.conversation.otherUserId) {
          setSelectedConversation(null);
          setMessages([]);
        }
        
        setDeleteDialog({ open: false, conversation: null });
      } else {
        console.error('Failed to delete conversation');
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const handleDeleteClick = (conversation: Conversation, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent selecting the conversation
    setDeleteDialog({ open: true, conversation });
  };

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        Messages
      </Typography>
      
      <Grid container sx={{ flex: 1, overflow: 'hidden' }}>
        {/* Conversations List */}
        <Grid item xs={12} md={4} sx={{ borderRight: 1, borderColor: 'divider' }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Conversations
            </Typography>
            <List>
              {conversations.map((conversation) => (
                <ListItem
                  key={conversation.otherUserId}
                  button
                  onClick={() => selectConversation(conversation)}
                  selected={selectedConversation?.otherUserId === conversation.otherUserId}
                  sx={{ borderRadius: 1, mb: 1 }}
                >
                  <ListItemAvatar>
                    <Badge
                      color="error"
                      variant="dot"
                      invisible={!conversation.hasUnreadMessages}
                    >
                      <Avatar>
                        <PersonIcon />
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={conversation.otherUser.name}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary" noWrap>
                          {conversation.messageCount} messages
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(conversation.lastMessageAt).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(conversation, e)}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Grid>

        {/* Messages Area */}
        <Grid item xs={12} md={8} sx={{ display: 'flex', flexDirection: 'column' }}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h6">
                  {selectedConversation.otherUser.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedConversation.otherUser.email}
                </Typography>
              </Box>

              {/* Messages */}
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                {messages.map((message) => (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      justifyContent: message.senderId === user?.id ? 'flex-end' : 'flex-start',
                      mb: 2
                    }}
                  >
                    <Paper
                      sx={{
                        p: 2,
                        maxWidth: '70%',
                        backgroundColor: message.senderId === user?.id ? 'primary.main' : 'grey.100',
                        color: message.senderId === user?.id ? 'white' : 'text.primary'
                      }}
                    >
                      <Typography variant="body1">{message.content}</Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 1,
                          opacity: 0.7
                        }}
                      >
                        {new Date(message.createdAt).toLocaleTimeString()}
                      </Typography>
                    </Paper>
                  </Box>
                ))}
                <div ref={messagesEndRef} />
              </Box>

              {/* Message Input */}
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={loading}
                  />
                  <IconButton
                    color="primary"
                    onClick={sendMessage}
                    disabled={!newMessage.trim() || loading}
                  >
                    <SendIcon />
                  </IconButton>
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="h6" color="textSecondary">
                Select a conversation to start messaging
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, conversation: null })}>
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this conversation with {deleteDialog.conversation?.otherUser.name}? 
            This action cannot be undone and all messages will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, conversation: null })}>
            Cancel
          </Button>
          <Button onClick={deleteConversation} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewMessaging;
